import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GetOptions } from '@dto/commons/GetOptions';
import { ObjectUtils } from '@utils/object.utils';
import { StringUtils } from '@utils/string.utils';

export abstract class BaseHttpService {

  protected endpoint: string;

  protected constructor(
    protected http: HttpClient,
    endpoint: string
  ) {
    this.endpoint = endpoint;
  }

  post<Req, Res>(payload: Req, postfix?: string): Promise<Res> {
    return this.sendRequest<Req, Res>('post', postfix, payload);
  }

  put<Req, Res>(payload: Req, postfix?: string): Promise<Res> {
    return this.sendRequest<Req, Res>('put', postfix, payload);
  }

  patch<Req, Res>(payload: Req, postfix?: string): Promise<Res> {
    return this.sendRequest<Req, Res>('patch', postfix, payload);
  }

  /** @param cacheable skip the anti-cache `r` param so the browser can honor Cache-Control */
  get<T>(options?: GetOptions<any>, postfix?: string | number, cacheable = false): Promise<T> {
    return this.sendRequest<null, T>('get', postfix, null, options, cacheable);
  }

  delete<T = void>(postfix?: string | number): Promise<T> {
    return this.sendRequest<null, T>('delete', postfix);
  }

  sendRequest<Req, Res>(
    method: 'post' | 'put' | 'patch' | 'get' | 'delete',
    postfix?: string | number,
    body?: Req,
    query?: GetOptions<any>,
    cacheable = false
  ): Promise<Res> {
    const params = this.buildQuery(query, cacheable);
    const url = [this.endpoint, postfix]
      .map(StringUtils.trimSlash)
      .filter(Boolean)
      .join('/');

    const options = { params };

    const req = ['get', 'delete'].includes(method)
      ? this.http[method as 'get']<Res>(url, options)
      : this.http[method as 'post']<Res>(url, body, options);

    return this.toPromise(req);
  }

  protected toPromise<T>(req: Observable<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const s = req.subscribe({
        next: p => {
          s?.unsubscribe();
          resolve(p);
        },
        error: e => {
          s?.unsubscribe();
          reject(e);
        }
      });
    });
  }

  protected buildQuery<T extends {}>(payload?: T, cacheable = false): HttpParams {
    const fromObject = payload ? ObjectUtils.cutEmpty(payload) as Record<string, any> : {};
    if (!cacheable) {
      fromObject['r'] = new Date().getTime() % 1000;
    }
    Object.keys(fromObject).forEach(key => {
      fromObject[key] = String(fromObject[key]);
    });
    return new HttpParams({ fromObject });
  }
}
